/**
 * Field Trip / 360 View Content Slide
 * Displays panoramic 360° views via Pannellum (uploaded images) or iframe (external URLs).
 *
 * External URLs are handled as follows:
 * - YouTube / Vimeo / Dailymotion links are converted to their embed equivalents.
 * - Google Maps / Street View embeds and other known embeddable providers
 *   are passed through directly.
 * - All other URLs are loaded directly; if the iframe fails to load the
 *   user is shown a fallback with an "Open in new tab" link.
 * - Sites that are known to aggressively block iframes show the fallback
 *   immediately.
 */

import { useEffect, useRef, useState } from 'react';
import type { ContentSlideProps } from '../types';
import { PannellumViewer } from '../../common/PannellumViewer';
import { stripHtml } from '../../../../utils/slides/stripHtml';

const RICH_TEXT_STYLES = `
  .rich-text-content h1 {
    font-size: 2em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .rich-text-content h2 {
    font-size: 1.5em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .rich-text-content h3 {
    font-size: 1.25em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .rich-text-content strong {
    font-weight: bold;
  }
  .rich-text-content em {
    font-style: italic;
  }
  .rich-text-content u {
    text-decoration: underline;
  }
  .rich-text-content ul {
    list-style-type: disc;
    padding-left: 2em;
    margin-bottom: 1em;
  }
  .rich-text-content ol {
    list-style-type: decimal;
    padding-left: 2em;
    margin-bottom: 1em;
  }
  .rich-text-content li {
    margin-bottom: 0.5em;
  }
  .rich-text-content p {
    margin-bottom: 0.5em;
  }
  .rich-text-content .ql-align-center {
    text-align: center;
  }
  .rich-text-content .ql-align-right {
    text-align: right;
  }
  .rich-text-content .ql-align-left {
    text-align: left;
  }
  .rich-text-content .ql-align-justify {
    text-align: justify;
  }
`;

/* ------------------------------------------------------------------ */
/*  URL helpers                                                       */
/* ------------------------------------------------------------------ */

/** Domains that aggressively frame-bust — show fallback immediately. */
const NON_EMBEDDABLE_HOSTS = [
  'accounts.google.com',
  'mail.google.com',
  'drive.google.com',
  'facebook.com',
  'www.facebook.com',
  'twitter.com',
  'x.com',
  'instagram.com',
  'www.instagram.com',
  'linkedin.com',
  'www.linkedin.com',
  'github.com',
  'login.microsoftonline.com',
  'outlook.live.com',
  'outlook.office.com',
];

function isKnownNonEmbeddable(url: string): boolean {
  try {
    const hostname = new URL(url).hostname;
    return NON_EMBEDDABLE_HOSTS.some(
      (h) => hostname === h || hostname.endsWith(`.${h}`),
    );
  } catch {
    return false;
  }
}

/* ---- Video embed converters ---- */

/**
 * Convert a regular YouTube / YouTube Shorts / youtu.be URL to its embed form.
 * Returns `null` if the URL is not a YouTube link.
 */
function toYouTubeEmbed(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace('www.', '');
    let videoId: string | null = null;

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (parsed.pathname.startsWith('/embed/')) return url; // already embed
      if (parsed.pathname === '/watch') {
        videoId = parsed.searchParams.get('v');
      } else if (parsed.pathname.startsWith('/shorts/')) {
        videoId = parsed.pathname.split('/shorts/')[1]?.split(/[/?#]/)[0] ?? null;
      } else if (parsed.pathname.startsWith('/live/')) {
        videoId = parsed.pathname.split('/live/')[1]?.split(/[/?#]/)[0] ?? null;
      }
    } else if (host === 'youtu.be') {
      videoId = parsed.pathname.slice(1).split(/[/?#]/)[0] ?? null;
    }

    if (!videoId) return null;

    const embed = new URL(`https://www.youtube.com/embed/${videoId}`);
    const start = parsed.searchParams.get('t') || parsed.searchParams.get('start');
    if (start) embed.searchParams.set('start', start.replace('s', ''));
    return embed.toString();
  } catch {
    return null;
  }
}

/** Convert a Vimeo URL to its embed form. */
function toVimeoEmbed(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace('www.', '');
    if (host !== 'vimeo.com') return null;
    if (parsed.pathname.startsWith('/video/')) return url; // already embed-ish
    const match = parsed.pathname.match(/^\/(\d+)/);
    if (!match) return null;
    return `https://player.vimeo.com/video/${match[1]}`;
  } catch {
    return null;
  }
}

/** Convert a Dailymotion URL to its embed form. */
function toDailymotionEmbed(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace('www.', '');
    if (host !== 'dailymotion.com' && host !== 'dai.ly') return null;
    let videoId: string | null = null;
    if (host === 'dai.ly') {
      videoId = parsed.pathname.slice(1).split(/[/?#]/)[0] ?? null;
    } else {
      const match = parsed.pathname.match(/\/video\/([A-Za-z0-9]+)/);
      videoId = match ? match[1] : null;
    }
    if (!videoId) return null;
    return `https://www.dailymotion.com/embed/video/${videoId}`;
  } catch {
    return null;
  }
}

/** Convert a Google Maps share/place URL to an embeddable URL. */
function toGoogleMapsEmbed(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace('www.', '');
    // Already an embed URL
    if (host === 'google.com' && parsed.pathname.startsWith('/maps/embed')) return url;
    // maps.google.com or google.com/maps — use maps embed API with the full URL
    if (
      host === 'maps.google.com' ||
      host === 'goo.gl' ||
      (host === 'google.com' && parsed.pathname.startsWith('/maps'))
    ) {
      return `https://maps.google.com/maps?output=embed&q=${encodeURIComponent(url)}`;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Resolve the best embeddable iframe src for a given URL.
 * Returns `{ src, needsFullAccess, blocked }`.
 *
 * - `needsFullAccess`: true for video embeds that need full origin access
 *   (YouTube, Vimeo etc.) so we skip the restrictive `sandbox` attribute.
 * - `blocked`: true when the site is known to block iframes entirely.
 */
function resolveIframeSrc(rawUrl: string): {
  src: string;
  needsFullAccess: boolean;
  blocked: boolean;
} {
  if (isKnownNonEmbeddable(rawUrl)) {
    return { src: rawUrl, needsFullAccess: false, blocked: true };
  }

  // Try video embed converters
  const ytEmbed = toYouTubeEmbed(rawUrl);
  if (ytEmbed) return { src: ytEmbed, needsFullAccess: true, blocked: false };

  const vimeoEmbed = toVimeoEmbed(rawUrl);
  if (vimeoEmbed) return { src: vimeoEmbed, needsFullAccess: true, blocked: false };

  const dmEmbed = toDailymotionEmbed(rawUrl);
  if (dmEmbed) return { src: dmEmbed, needsFullAccess: true, blocked: false };

  // Try Google Maps conversion
  const mapsEmbed = toGoogleMapsEmbed(rawUrl);
  if (mapsEmbed) return { src: mapsEmbed, needsFullAccess: false, blocked: false };

  // Default: load the URL directly — the iframe's onError / timeout
  // will trigger the "Open in new tab" fallback if the site blocks it.
  return { src: rawUrl, needsFullAccess: false, blocked: false };
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function FieldTripSlide({ slide, className = '' }: ContentSlideProps) {
  const { content } = slide;

  const mode = content?.panoramaMode || 'url';
  const hasUploadedImage = mode === 'upload' && content?.panoramaImageUrl;
  const hasExternalUrl = content?.panoramaUrl;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const resolved = hasExternalUrl
    ? resolveIframeSrc(content.panoramaUrl)
    : null;

  // Fallback timer – if the iframe hasn't fired onLoad after 15 s treat as stalled
  useEffect(() => {
    if (!resolved || resolved.blocked) return;
    setLoading(true);
    setError(false);
    timeoutRef.current = setTimeout(() => {
      if (loading) setLoading(false);
    }, 15_000);
    return () => clearTimeout(timeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved?.src]);

  const handleLoad = () => {
    clearTimeout(timeoutRef.current);
    setLoading(false);
  };
  const handleError = () => {
    clearTimeout(timeoutRef.current);
    setLoading(false);
    setError(true);
  };

  const showFallback = resolved?.blocked || error;

  return (
    <div className={`mb-8 max-w-3xl mx-auto ${className}`}>
      <style>{RICH_TEXT_STYLES}</style>
      {content?.showTitle !== false && content?.title && (
        <h2 className="text-3xl font-bold mb-4 text-center">
          {stripHtml(content.title)}
        </h2>
      )}
      {content?.location && (
        <p className="text-xl font-semibold mb-2 text-accent">
          📍 {content.location}
        </p>
      )}
      {content?.description && (
        <div className="text-lg mb-4 text-foreground rich-text-content max-w-none" dangerouslySetInnerHTML={{ __html: content.description }} />
      )}

      {/* Uploaded 360° image → Pannellum viewer */}
      {hasUploadedImage && (
        <PannellumViewer
          imageUrl={content.panoramaImageUrl}
          className="w-full rounded-2xl border-2 border-border shadow-lg h-[55vh] min-h-[320px] max-h-[75vh]"
        />
      )}

      {/* External URL */}
      {!hasUploadedImage && hasExternalUrl && resolved && (
        <>
          {showFallback ? (
            <div className="w-full h-[55vh] min-h-[320px] max-h-[75vh] rounded-2xl border-2 border-border shadow-lg bg-muted flex flex-col items-center justify-center gap-4 px-6 text-center">
              <div className="text-4xl">🌐</div>
              <p className="text-lg font-semibold text-foreground">
                This site cannot be embedded
              </p>
              <p className="text-sm text-muted-foreground max-w-md">
                {resolved.blocked
                  ? 'This website does not allow embedding. You can open it directly in a new tab.'
                  : 'The site may be unavailable, require a login, or block embedding.'}
              </p>
              <a
                href={content.panoramaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Open in new tab ↗
              </a>
            </div>
          ) : (
            <div className="relative">
              {loading && (
                <div className="absolute inset-0 rounded-2xl bg-muted flex items-center justify-center z-10">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-muted-foreground">Loading 360° view…</p>
                  </div>
                </div>
              )}
              <iframe
                ref={iframeRef}
                src={resolved.src}
                className="w-full h-[55vh] min-h-[320px] max-h-[75vh] rounded-2xl border-2 border-border shadow-lg"
                title="360° Field Trip"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; xr-spatial-tracking"
                allowFullScreen
                sandbox={
                  resolved.needsFullAccess
                    ? undefined /* Video embeds need full context to work */
                    : 'allow-scripts allow-same-origin allow-forms allow-popups'
                }
                referrerPolicy="no-referrer-when-downgrade"
                onLoad={handleLoad}
                onError={handleError}
                loading="lazy"
              />
              <div className="mt-2 text-right">
                <a
                  href={content.panoramaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                >
                  Open in new tab ↗
                </a>
              </div>
            </div>
          )}
        </>
      )}

      {/* No content configured */}
      {!hasUploadedImage && !hasExternalUrl && (
        <div className="w-full h-[45vh] min-h-[240px] max-h-[70vh] rounded-2xl border-2 border-dashed border-border flex items-center justify-center text-muted-foreground">
          No 360° content configured
        </div>
      )}
    </div>
  );
}
